import React, { useState, useEffect, useCallback } from 'react';
import GlassModal from '../ui/GlassModal';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import GlassInput from '../ui/GlassInput';
import { bookingApi } from '../../services/api';
import { brandingVars, BookingBranding } from './bookingBranding';

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

// Same field list/order as FormStyleTab.tsx's COLOR_FIELDS, plus the booking-only logo_url key
// (handled separately, below, via the logo picker).
const COLOR_FIELDS: Array<{ key: keyof BookingBranding; label: string; fallback: string }> = [
  { key: 'header_color', label: 'Header Color', fallback: '#0F172A' },
  { key: 'text_color', label: 'Text Color', fallback: '#0F172A' },
  { key: 'background_color', label: 'Card Background', fallback: '#FFFFFF' },
  { key: 'page_background', label: 'Page Background', fallback: '#FFFFFF' },
  { key: 'input_background', label: 'Input Background', fallback: '#FFFFFF' },
  { key: 'input_text_color', label: 'Input Text Color', fallback: '#0F172A' },
  { key: 'border_color', label: 'Border Color', fallback: '#E2E8F0' },
  { key: 'button_color', label: 'Button Color', fallback: '#2177FF' },
  { key: 'button_text_color', label: 'Button Text Color', fallback: '#FFFFFF' },
];

interface LogoOption {
  key: string;
  label: string;
  url: string;
}

interface BookingBrandingModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string;
}

const BookingBrandingModal: React.FC<BookingBrandingModalProps> = ({ isOpen, onClose, projectId, projectName }) => {
  const [style, setStyle] = useState<BookingBranding>({});
  const [brandDefault, setBrandDefault] = useState<BookingBranding | null>(null);
  const [savedDefault, setSavedDefault] = useState<BookingBranding | null>(null);
  const [logoOptions, setLogoOptions] = useState<LogoOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadDefaults = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const res = await bookingApi.getBrandingDefaults(projectId);
      setBrandDefault(res.brand);
      setSavedDefault(res.saved_default);
      setLogoOptions(res.logo_options ?? []);
      // Seed the editor with the saved preset if one exists, else the resolved value (which,
      // absent a saved preset, is the same object as `brand`), else an empty style.
      setStyle(res.saved_default ?? res.resolved ?? {});
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load branding defaults.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccess('');
      loadDefaults();
    }
  }, [isOpen, loadDefaults]);

  const updateField = (key: keyof BookingBranding, value: string) => {
    setStyle((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const saveAsProjectDefault = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await bookingApi.saveBrandingDefault(projectId, style);
      setSavedDefault(res.style);
      setSuccess('Saved as your project default — live on the public booking pages now.');
    } catch (err: any) {
      setError(getApiError(err, 'Could not save project default.'));
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm('Clear the saved branding preset for this project? The booking pages will fall back to the Brand Guide colours (or the default AIME look if there is no Brand Guide).')) {
      return;
    }
    setClearing(true);
    setError('');
    setSuccess('');
    try {
      const res = await bookingApi.saveBrandingDefault(projectId, null);
      setSavedDefault(null);
      setStyle(res.style ?? {});
      setSuccess('Override cleared.');
    } catch (err: any) {
      setError(getApiError(err, 'Could not clear the override.'));
    } finally {
      setClearing(false);
    }
  };

  const resetToBrandColours = () => {
    setStyle(brandDefault ?? {});
  };

  const previewVars = brandingVars(style);

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Booking Page Branding${projectName ? ` — ${projectName}` : ''}`}
      size="lg"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', width: '100%' }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
            Until you save, the booking pages follow your Brand Guide live — editing the Brand Guide instantly
            restyles them. Saving pins these exact values as the project's override, so future Brand Guide
            changes will no longer apply here until you clear the override.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
            {savedDefault && (
              <GlassButton variant="secondary" onClick={clearOverride} disabled={clearing || saving}>
                {clearing ? 'Clearing…' : 'Clear Override'}
              </GlassButton>
            )}
            <GlassButton variant="secondary" onClick={onClose}>
              Cancel
            </GlassButton>
            <GlassButton variant="primary" onClick={saveAsProjectDefault} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save as Project Default'}
            </GlassButton>
          </div>
        </div>
      }
    >
      {loading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {brandDefault && (
            <div>
              <GlassButton variant="secondary" onClick={resetToBrandColours}>
                Reset to Brand Colours
              </GlassButton>
            </div>
          )}

          <GlassCard>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600 }}>Colours</h4>
            <div className="color-inputs">
              {COLOR_FIELDS.map(({ key, label, fallback }) => (
                <div className="form-group" key={key}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    {label}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="color"
                      value={(style[key] as string) || fallback}
                      onChange={(e) => updateField(key, e.target.value)}
                      style={{
                        width: '48px', height: '48px', border: '1px solid var(--stroke)',
                        borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer',
                      }}
                    />
                    <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                      {(style[key] as string) || 'unset'}
                    </span>
                    {style[key] && (
                      <button
                        type="button"
                        onClick={() => updateField(key, '')}
                        style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '20px' }}>
              <GlassInput
                label="Font Family"
                value={style.font_family || ''}
                onChange={(v) => updateField('font_family', v)}
                placeholder="e.g. Georgia (falls back to the visitor's system font if not installed)"
              />
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                Names a font family — doesn't load one. If the visitor's device doesn't have it installed, the page falls back to its default font stack.
              </p>
            </div>
          </GlassCard>

          <GlassCard>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Logo</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '14px', color: 'var(--text)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="bk-logo-option"
                  checked={!style.logo_url}
                  onChange={() => updateField('logo_url', '')}
                />
                No logo
              </label>
              {logoOptions.map((opt) => (
                <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '14px', color: 'var(--text)', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="bk-logo-option"
                    checked={style.logo_url === opt.url}
                    onChange={() => updateField('logo_url', opt.url)}
                  />
                  {opt.label}
                  <img
                    src={opt.url}
                    alt={opt.label}
                    style={{ height: '24px', maxWidth: '100px', objectFit: 'contain' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </label>
              ))}
              {logoOptions.length === 0 && (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                  No logo variants found on this project's Brand Guide.
                </p>
              )}
            </div>
          </GlassCard>

          <GlassCard>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Preview</h4>
            <div
              className="bk-scope"
              style={{
                ...previewVars,
                // Preview shows what the public (unauthenticated) booking page will actually
                // look like, which always forces a light background regardless of the admin's
                // own theme — see the matching .bk-page fallback in Booking.css. Only this inner
                // preview box is pinned; the surrounding modal chrome keeps following the admin's
                // theme as usual.
                background: 'var(--bk-page-bg, #f4f7f9)',
                fontFamily: 'var(--bk-font, inherit)',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div className="bk-card" style={{ maxWidth: 'none' }}>
                {style.logo_url && (
                  <img
                    className="bk-logo"
                    src={style.logo_url}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <h1 className="bk-title">Sample Meeting</h1>
                <p className="bk-desc">A 30-minute consultation.</p>
                <div className="bk-field">
                  <label className="bk-label">Name</label>
                  <input className="bk-input" type="text" readOnly value="Jane Smith" />
                </div>
                <button type="button" className="bk-btn-primary" style={{ width: '100%' }}>
                  Book
                </button>
              </div>
            </div>
          </GlassCard>

          {error && <div className="fi-error">{error}</div>}
          {success && (
            <div style={{ color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)' }}>
              {success}
            </div>
          )}
        </div>
      )}
    </GlassModal>
  );
};

export default BookingBrandingModal;
